import React, { useState, useContext } from 'react';
import styled, { css } from 'styled-components';
import { useMutation, gql } from '@apollo/client';
import axios from 'axios';
import { handleInputChange, handleFileChange, resetForm } from '../utils/formHandlers';
import { getCookie } from '../utils/cookies';
import { Web3Context } from '../context/Web3Context';
import {createTrackOnBlockchain} from '../utils/blockchain';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';

// Define the GraphQL mutation
const CREATE_MUSIC_TRACK_MUTATION = gql`
  mutation CreateMusicTrack(
    $trackHolder: String!,
    $artist: String!,
    $songName: String!,
    $recordLabel: String!,
    $bpm: String!,
    $genre: String!,
    $releaseDate: String!,
    $format: String!,
    $length: Int!,
    $imageUrl: String!,
    $musicUrl: String!,
    $imageIv: String!,
    $musicIv: String!
  ) {
    createMusicTrack(
      trackHolder: $trackHolder,
      artist: $artist,
      songName: $songName,
      recordLabel: $recordLabel,
      bpm: $bpm,
      genre: $genre,
      releaseDate: $releaseDate,
      format: $format,
      length: $length,
      imageUrl: $imageUrl,
      musicUrl: $musicUrl,
      imageIv: $imageIv,
      musicIv: $musicIv
    ) {
      id
      trackHolder
      artist
      songName
      recordLabel
      bpm
      genre
      releaseDate
      format
      length
      imageUrl
      musicUrl
      imageIv
      musicIv
    }
  }
`;

// Styled components with responsive design adjustments
const PageContainer = styled.div`
  padding: 20px;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  background: ${({ theme }) => theme.colors.background};
  border-radius: 10px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 10px;
    border-radius: 5px;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  margin: 10px 0 5px;
  font-weight: bold;
`;

const inputStyles = css`
  padding: 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 5px;
  margin-bottom: 10px;
`;

const Input = styled.input`${inputStyles}`;
const TextArea = styled.textarea`${inputStyles}`;

const Button = styled.button`
  padding: 10px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-weight: bold;
  margin-bottom: 10px;
  color: ${({ theme }) => theme.colors.secondary};

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
  }
`;

const SubmitButton = styled(Button)`
  background-color: ${({ theme }) => theme.colors.accent};
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const EditButton = styled(Button)`
  background-color: ${({ theme }) => theme.colors.secondary};
  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.secondary};
  }
`;

const PreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
`;

const ImagePreview = styled.img`
  width: 100%;
  max-height: 300px;
  object-fit: cover;
  border-radius: 5px;
  margin-bottom: 20px;
`;

const AudioPreview = styled.audio`
  width: 100%;
  margin-bottom: 20px;
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const LoadingSpinner = styled.div`
  border: 5px solid #f3f3f3;
  border-top: 5px solid #3498db;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const CreatorPage = () => {
  const { account } = useContext(Web3Context);
  const [formState, setFormState] = useState({
    trackHolder: account || '',
    artist: '',
    songName: '',
    recordLabel: '',
    bpm: '',
    genre: '',
    releaseDate: '',
    format: '',
    length: '',
    musicFile: null,
    imageFile: null,
  });

  const [previews, setPreviews] = useState({
    musicPreview: null,
    imagePreview: null,
  });

  const [editMode, setEditMode] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploading, setUploading] = useState(false);

  const [createMusicTrack, { error: mutationError }] = useMutation(CREATE_MUSIC_TRACK_MUTATION);

  const handleSubmit = async (event) => {
    event.preventDefault();

    confirmAlert({
      title: 'Confirm submission',
      message: 'Are you sure you want to upload this track?',
      buttons: [
        {
          label: 'Yes',
          onClick: () => submitTrack()
        },
        {
          label: 'No',
          onClick: () => {}
        }
      ]
    });
  };

  const submitTrack = async () => {
    const { musicFile, imageFile, length } = formState;

    if (!musicFile || !imageFile) {
      setError('Please upload both a music file and an image.');
      return;
    }

    if (length > 30) {
      setError('Music length cannot exceed 30 minutes.');
      return;
    }

    try {
      setError('');
      setSuccess('');
      setUploading(true);

      const formData = new FormData();
      const musicBlob = new Blob([musicFile], { type: musicFile.type });
      const imageBlob = new Blob([imageFile], { type: imageFile.type });
      formData.append('musicFile', musicBlob, musicFile.name);
      formData.append('imageFile', imageBlob, imageFile.name);

      const token = getCookie('token');

      console.log('Uploading files...');
      const uploadResponse = await axios.post(`${process.env.REACT_APP_GRAPHQL_ENDPOINT}/api/upload`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true
      });

      console.log('Upload response:', uploadResponse);

      if (uploadResponse.status !== 200) {
        const errorText = uploadResponse.data;
        console.error('Upload error:', errorText);
        throw new Error(`Failed to upload files: ${errorText}`);
      }

      const { musicHash, imageHash, musicIv, imageIv } = uploadResponse.data;

      if (!musicHash || !imageHash || !musicIv || !imageIv) {
        throw new Error('Upload response is missing required fields: musicHash, imageHash, musicIv, or imageIv.');
      }

      // Save the metadata to the database
      const result = await createMusicTrack({
        variables: {
          trackHolder: formState.trackHolder,
          artist: formState.artist,
          songName: formState.songName,
          recordLabel: formState.recordLabel,
          bpm: formState.bpm,
          genre: formState.genre,
          releaseDate: formState.releaseDate,
          format: formState.format,
          length: parseInt(length, 10),
          imageUrl: `https://gateway.pinata.cloud/ipfs/${imageHash}`,
          musicUrl: `https://gateway.pinata.cloud/ipfs/${musicHash}`,
          imageIv,
          musicIv,
        },
      });

      // Log the track on the blockchain
      const trackId = result.data.createMusicTrack.id;
      await createTrackOnBlockchain(trackId);

      setSuccess('Music track uploaded, metadata posted, and logged on blockchain successfully.');
      resetForm(setFormState, setPreviews);
    } catch (error) {
      console.error('Upload failed:', error);
      setError(`Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = () => {
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setSuccess('');
  };

  return (
    <PageContainer>
      <h1>Upload Music</h1>
      <Form onSubmit={handleSubmit}>
        {!formState.musicFile || !formState.imageFile ? (
          <>
            <Label>Music File</Label>
            <Input
              type="file"
              accept="audio/*"
              onChange={(e) => handleFileChange(e, 'musicFile', 'musicPreview', setFormState, setPreviews)}
            />

            <Label>Image File</Label>
            <Input
              type="file"
              accept="image/*"
              onChange={(e) => handleFileChange(e, 'imageFile', 'imagePreview', setFormState, setPreviews)}
            />
          </>
        ) : !editMode ? (
          <>
            <PreviewContainer>
              <ImagePreview src={previews.imagePreview} alt="Image Preview" />
              <AudioPreview controls src={previews.musicPreview} />
            </PreviewContainer>

            {['trackHolder', 'artist', 'songName', 'recordLabel', 'bpm', 'genre', 'releaseDate', 'format', 'length'].map((field) => (
              <React.Fragment key={field}>
                <Label>{field.charAt(0).toUpperCase() + field.slice(1)}</Label>
                <Input
                  type={field === 'length' ? 'number' : field === 'releaseDate' ? 'date' : 'text'}
                  name={field}
                  value={formState[field]}
                  onChange={(e) => handleInputChange(e, setFormState)}
                />
              </React.Fragment>
            ))}

            <SubmitButton type="submit" disabled={uploading}>Submit</SubmitButton>
            <EditButton type="button" onClick={handleEdit}>Edit</EditButton>
          </>
        ) : (
          <>
            <Label>Music File</Label>
            <Input
              type="file"
              accept="audio/*"
              onChange={(e) => handleFileChange(e, 'musicFile', 'musicPreview', setFormState, setPreviews)}
            />
  
            <Label>Image File</Label>
            <Input
              type="file"
              accept="image/*"
              onChange={(e) => handleFileChange(e, 'imageFile', 'imagePreview', setFormState, setPreviews)}
            />
  
            {['trackHolder', 'artist', 'songName', 'recordLabel', 'bpm', 'genre', 'releaseDate', 'format', 'length'].map((field) => (
              <React.Fragment key={field}>
                <Label>{field.charAt(0).toUpperCase() + field.slice(1)}</Label>
                <Input
                  type={field === 'length' ? 'number' : field === 'releaseDate' ? 'date' : 'text'}
                  name={field}
                  value={formState[field]}
                  onChange={(e) => handleInputChange(e, setFormState)}
                />
              </React.Fragment>
            ))}
  
            <SubmitButton type="submit" disabled={uploading}>Save</SubmitButton>
            <EditButton type="button" onClick={handleCancelEdit}>Cancel Edit</EditButton>
          </>
        )}
  
        {error && <p style={{ color: 'red' }}>{error}</p>}
        {success && <p style={{ color: 'green' }}>{success}</p>}
      </Form>
      {uploading && (
        <LoadingOverlay>
          <LoadingSpinner />
        </LoadingOverlay>
      )}
    </PageContainer>
  );
};

export default CreatorPage;